import type { DiagramType } from '../tools/interfaces/ITool';
import type { CanvasPlugin, CanvasPluginHostContext } from './CanvasPlugin';

type CanvasPluginLoader = (context: CanvasPluginHostContext) => Promise<CanvasPlugin> | CanvasPlugin;

type PluginRegistration = {
  id: string;
  diagramTypes: readonly DiagramType[];
  loader: CanvasPluginLoader;
};

const pluginRegistry = new Map<string, PluginRegistration>();
const pluginInstances = new Map<string, Promise<CanvasPlugin> | CanvasPlugin>();

export function registerCanvasPlugin(reg: PluginRegistration): void {
  pluginRegistry.set(reg.id, reg);
}

export async function loadCanvasPluginsForType(
  diagramType: DiagramType,
  contextBase: Omit<CanvasPluginHostContext, 'diagramType'>,
): Promise<CanvasPlugin[]> {
  const matches = Array.from(pluginRegistry.values()).filter((reg) => reg.diagramTypes.includes(diagramType));
  const context: CanvasPluginHostContext = { ...contextBase, diagramType };

  const loaded = await Promise.all(
    matches.map(async (reg) => {
      const existing = pluginInstances.get(reg.id);
      if (existing) return await Promise.resolve(existing);

      const pending = Promise.resolve(reg.loader(context));
      pluginInstances.set(reg.id, pending);

      try {
        const plugin = await pending;
        pluginInstances.set(reg.id, plugin);
        return plugin;
      } catch (error) {
        pluginInstances.delete(reg.id);
        throw error;
      }
    }),
  );

  return loaded;
}
